'use client'

import {
    faArrowRight,
    faBoxOpen,
    faCircleQuestion,
    faComments,
    faCreditCard,
    faHandSparkles,
    faHeadset,
    faSeedling,
    faTags,
    faTruck,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import Link from 'next/link'
import { BTN_BASE, BTN_SHADOW } from '@/components/Form/Button/legacyButtonClasses'
import Container from '@/components/Layout/Container'
import PageHero from '@/components/Pages/PageHero'
import { useHelpPages } from '@/hooks/business/pages/useHelpPages'
import { IPageNode } from '@/schemas/pages'
import PageSearch from './_components/PageSearch'

const ACCENTS = [
    'bg-healthy-green',
    'bg-accent',
    'bg-happy-yellow',
    'bg-jolly-rose',
    'bg-secondary',
    'bg-tertiary-green',
    'bg-primary',
]

function iconFor(node: IPageNode) {
    const key = `${node.slug} ${node.name}`.toLowerCase()
    if (key.includes('contato') || key.includes('atend')) return faHeadset
    if (key.includes('desconto') || key.includes('promo') || key.includes('cupom')) return faTags
    if (key.includes('entrega') || key.includes('frete')) return faTruck
    if (key.includes('novo')) return faSeedling
    if (key.includes('pagamento')) return faCreditCard
    if (key.includes('pedido')) return faBoxOpen
    if (key.includes('produto') || key.includes('luva')) return faHandSparkles
    return faCircleQuestion
}

export default function AjudaPage() {
    const { tree, isLoading, isError } = useHelpPages()

    const categories = tree.filter((n) => n.children.length > 0)
    const standalone = tree.filter((n) => n.children.length === 0)

    return (
        <div className="text-light-black mb-[120px] max-md:mb-[160px]">
            <PageHero title="central de" scriptTitle="ajuda" />

            {/* Busca em destaque, sobreposta ao hero */}
            <Container className="px-4 -mt-10 relative z-50">
                <div className="bg-white rounded-3xl shadow-xl border border-gray-100 p-6 md:p-8">
                    <h2 className="text-primary font-bold text-lg md:text-xl mb-1">Olá! Como podemos te ajudar?</h2>
                    <p className="text-sm text-gray-500 mb-5">
                        Encontre respostas rápidas nas nossas categorias ou busque por um assunto.
                    </p>
                    <PageSearch />
                </div>
            </Container>

            <Container className="px-4 mt-12">
                {isError && (
                    <p className="text-sm text-gray-500 px-1 mb-6">
                        Não foi possível carregar as categorias agora. Use a busca ou fale com o atendimento.
                    </p>
                )}

                {isLoading && categories.length === 0 && (
                    <div className="grid grid-cols-3 max-lg:grid-cols-2 max-md:grid-cols-1 gap-6">
                        {[0, 1, 2, 3, 4, 5].map((i) => (
                            <div key={i} className="h-44 rounded-2xl bg-gray-100 animate-pulse" />
                        ))}
                    </div>
                )}

                <div className="grid grid-cols-3 max-lg:grid-cols-2 max-md:grid-cols-1 gap-6">
                    {categories.map((cat, idx) => {
                        const accent = ACCENTS[idx % ACCENTS.length]
                        const visible = cat.children.slice(0, 4)
                        const rest = cat.children.length - visible.length
                        return (
                            <div
                                key={cat.id}
                                className="relative flex flex-col bg-white border rounded-2xl shadow-xs hover:shadow-lg hover:-translate-y-1 transition-all duration-200"
                            >
                                <div className="flex flex-auto flex-col gap-2 p-6 [&_p]:grow">
                                    <div className="flex items-center gap-3 mb-4">
                                        <span
                                            className={`w-12 h-12 rounded-2xl flex items-center justify-center text-white text-lg shrink-0 ${accent}`}
                                        >
                                            <FontAwesomeIcon icon={iconFor(cat)} />
                                        </span>
                                        <div className="min-w-0">
                                            <Link
                                                href={`/p/${cat.slug}`}
                                                className="block font-bold text-primary text-base leading-tight hover:text-secondary"
                                            >
                                                {cat.name}
                                            </Link>
                                            <span className="inline-flex items-center justify-center transition-[color,background-color,border-color,text-decoration-color,fill,stroke,opacity,box-shadow,transform,filter,backdrop-filter] duration-200 ease-[cubic-bezier(0,0,0.2,1)] h-5 w-fit px-[0.563rem] rounded-[1.9rem] leading-5 border-none border-base-200 bg-base-100 text-base-content text-[11px] mt-1">
                                                {cat.children.length} {cat.children.length === 1 ? 'artigo' : 'artigos'}
                                            </span>
                                        </div>
                                    </div>

                                    <ul className="flex flex-col gap-2.5">
                                        {visible.map((child) => (
                                            <li key={child.id}>
                                                <Link
                                                    href={`/p/${child.slug}`}
                                                    className="group flex items-center gap-2 text-sm text-gray-600 hover:text-primary"
                                                >
                                                    <FontAwesomeIcon
                                                        icon={faArrowRight}
                                                        className="text-[10px] text-gray-300 group-hover:text-tertiary-green transition"
                                                    />
                                                    <span className="line-clamp-1">{child.name}</span>
                                                </Link>
                                            </li>
                                        ))}
                                    </ul>

                                    <Link
                                        href={`/p/${cat.slug}`}
                                        className="inline-flex items-center gap-2 text-sm font-bold text-secondary hover:text-primary mt-4"
                                    >
                                        {rest > 0 ? `ver todos (${cat.children.length})` : 'ver categoria'}
                                        <FontAwesomeIcon icon={faArrowRight} className="text-xs" />
                                    </Link>
                                </div>
                            </div>
                        )
                    })}
                </div>

                {standalone.length > 0 && (
                    <section className="mt-12">
                        <h2 className="subtitulo-h2 mb-4">Conteúdos</h2>
                        <div className="flex flex-wrap gap-3">
                            {standalone.map((p) => (
                                <Link
                                    key={p.id}
                                    href={`/p/${p.slug}`}
                                    className="px-4 py-2 rounded-full border border-gray-200 text-sm text-gray-600 hover:border-tertiary-green hover:text-primary transition"
                                >
                                    {p.name}
                                </Link>
                            ))}
                        </div>
                    </section>
                )}

                {/* CTA */}
                <div className="mt-14 rounded-3xl bg-healthy-green-ultralight p-8 md:p-10 flex max-md:flex-col items-center justify-between gap-6">
                    <div className="flex items-center gap-4">
                        <span className="w-14 h-14 rounded-2xl bg-healthy-green text-white flex items-center justify-center text-2xl shrink-0">
                            <FontAwesomeIcon icon={faComments} />
                        </span>
                        <div>
                            <h3 className="font-bold text-primary text-lg">Não encontrou o que procurava?</h3>
                            <p className="text-sm text-gray-600">Fale com o nosso time de atendimento, é rapidinho.</p>
                        </div>
                    </div>
                    <Link
                        href="https://wa.me/554135153980?text=Ol%C3%A1!%20preciso%20de%20ajuda."
                        target="_blank"
                        rel="noopener noreferrer"
                        className={`${BTN_BASE} ${BTN_SHADOW} h-12 min-h-12 px-8 text-sm leading-[1em] font-[600] border-none border-base-200 outline-base-content bg-healthy-green hover:bg-primary-green text-white rounded-full normal-case`}
                    >
                        Falar com atendimento
                    </Link>
                </div>
            </Container>
        </div>
    )
}
